import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Trash2, History, Pencil, Save, X } from "lucide-react";

const KIND_TABS = [
  { v: "story", label: "Joy Stories" },
  { v: "help_request", label: "Help Requests" },
  { v: "housing_project", label: "Housing Projects" },
  { v: "event", label: "Events" },
  { v: "marketplace_listing", label: "Marketplace" },
  { v: "cause", label: "Causes" },
  { v: "recipe", label: "Recipes" },
  { v: "memorial", label: "Memorials" },
];

const EDITABLE_FIELDS = {
  story: ["title", "body", "author_name", "category", "city", "country", "image_url"],
  help_request: ["title", "description", "category", "urgency", "city", "country"],
  housing_project: ["title", "family_name", "description", "city", "country", "cost", "raised", "progress", "image_url"],
  event: ["title", "description", "venue", "city", "country", "start_date", "end_date", "admission", "cover_image"],
  marketplace_listing: ["title", "description", "category", "price", "city", "country", "status"],
  cause: ["title", "description", "category", "goal_amount", "raised_amount"],
  recipe: ["title", "description", "cuisine", "difficulty", "time_minutes", "servings"],
  memorial: ["full_name", "biography", "years_display", "city", "country", "cover_photo_url"],
};

export default function AdminSubmissions() {
  const [kind, setKind] = useState("story");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/submissions", { params: { kind, include_archived: true } });
      setItems(data.items);
    } catch (e) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const open = async (it) => {
    setSelected(it);
    try {
      const { data } = await http.get("/admin/events", { params: { entity_type: kind, entity_id: it.id, limit: 100 } });
      setHistory(data.items);
    } catch { setHistory([]); }
  };

  const archive = async (it) => {
    const comment = window.prompt("Reason for archiving (optional):") ?? "";
    try {
      await http.post(`/admin/submissions/${kind}/${it.id}/archive`, { comment });
      toast.success("Archived");
      load();
      if (selected?.id === it.id) open({ ...it, is_archived: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const unarchive = async (it) => {
    try {
      await http.post(`/admin/submissions/${kind}/${it.id}/unarchive`, { comment: "" });
      toast.success("Unarchived");
      load();
      if (selected?.id === it.id) open({ ...it, is_archived: false });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Permanently delete "${it.title}"? This cannot be undone.`)) return;
    const comment = window.prompt("Reason for deletion (recorded in audit log):") ?? "";
    try {
      await http.delete(`/admin/submissions/${kind}/${it.id}`, { params: { comment } });
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveEdit = async () => {
    const patch = {};
    for (const k of EDITABLE_FIELDS[kind]) {
      if (editing[k] !== selected[k]) patch[k] = editing[k];
    }
    if (Object.keys(patch).length === 0) { toast.info("No changes"); setEditing(null); return; }
    const comment = window.prompt("Reason for edit (recorded in audit log):") ?? "";
    try {
      const { data } = await http.patch(`/admin/submissions/${kind}/${selected.id}`, { patch, comment });
      toast.success("Saved");
      setEditing(null);
      setSelected(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const exportCSV = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/events?entity_type=${kind}&format=csv`;
    window.open(url, "_blank");
  };

  return (
    <div data-testid="admin-submissions-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">SUBMISSIONS</div>
          <h2 className="font-display text-3xl">User-authored content</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="admin-sub-bulk-delete"
            onClick={async () => {
              const match = window.prompt(`Permanently delete ALL ${kind} submissions whose title/body/name contains this phrase (case-insensitive). Type the phrase (e.g. "test"):`, "test");
              if (!match) return;
              if (!window.confirm(`Delete all ${kind}(s) containing "${match}"? This cannot be undone.`)) return;
              try {
                const { data } = await http.post(`/admin/submissions/${kind}/bulk-delete`, null, { params: { match } });
                toast.success(`Deleted ${data.deleted} ${kind}(s)`);
                load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
            }}
            className="wofy-btn-ghost text-sm"
            style={{ color: "#B23A3A" }}
            title={`Bulk-delete ${kind} entries by text match`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Bulk delete test entries
          </button>
          <button onClick={exportCSV} className="wofy-btn-ghost text-sm">Export CSV</button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {KIND_TABS.map((k) => (
          <button
            key={k.v}
            onClick={() => { setKind(k.v); setSelected(null); }}
            data-testid={`admin-sub-tab-${k.v}`}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${kind === k.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={kind === k.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : {}}
          >
            {k.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-3">
        {loading ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>None yet.</p>
        ) : items.map((it) => (
          <div key={it.id} data-testid={`admin-sub-${it.id}`} className={`wofy-card flex items-start gap-4 ${it.is_archived ? "opacity-60" : ""}`}>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="wofy-tag text-[10px]">{it.category || kind}</span>
                {it.is_archived && <span className="text-[10px] font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>Archived</span>}
              </div>
              <div className="font-display text-lg mt-1">{it.title}</div>
              {(it.description || it.body) && <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{it.description || it.body}</p>}
              <div className="mt-2 text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>
                {new Date(it.created_at).toLocaleString()} · {it.author_name || it.user_name || it.family_name || "—"} · {it.city || ""} {it.country || ""}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => open(it)} data-testid={`admin-sub-open-${it.id}`} className="wofy-btn-ghost text-xs">Manage</button>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 bg-[#14213D]/25 backdrop-blur-sm" onClick={() => { setSelected(null); setEditing(null); }}>
          <div
            className="absolute right-0 top-0 bottom-0 w-full max-w-[640px] overflow-y-auto p-8"
            style={{ background: "var(--wofy-paper)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-sub-drawer"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">{KIND_TABS.find((k) => k.v === kind)?.label}</div>
                <h3 className="font-display text-2xl mt-1">{selected.title}</h3>
                {selected.is_archived && <div className="mt-1 text-xs font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>Archived</div>}
              </div>
              <button onClick={() => { setSelected(null); setEditing(null); }} className="wofy-btn-ghost text-xs">Close</button>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap gap-2">
              {!editing && <button onClick={() => setEditing({ ...selected })} data-testid="admin-sub-edit" className="wofy-btn-ghost text-sm"><Pencil className="w-4 h-4" />Edit</button>}
              {selected.is_archived ? (
                <button onClick={() => unarchive(selected)} data-testid="admin-sub-unarchive" className="wofy-btn-ghost text-sm"><ArchiveRestore className="w-4 h-4" />Unarchive</button>
              ) : (
                <button onClick={() => archive(selected)} data-testid="admin-sub-archive" className="wofy-btn-ghost text-sm"><Archive className="w-4 h-4" />Archive</button>
              )}
              <button onClick={() => remove(selected)} data-testid="admin-sub-delete" className="wofy-btn-ghost text-sm" style={{ color: "#B23A3A" }}><Trash2 className="w-4 h-4" />Delete</button>
            </div>

            {/* Edit form */}
            {editing && (
              <div className="mt-6 wofy-card space-y-3">
                {EDITABLE_FIELDS[kind].map((f) => (
                  <label key={f} className="block">
                    <span className="text-[11px] font-mono uppercase tracking-wider">{f}</span>
                    {(f === "body" || f === "description") ? (
                      <textarea
                        rows={4}
                        value={editing[f] || ""}
                        onChange={(e) => setEditing({ ...editing, [f]: e.target.value })}
                        className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent"
                        style={{ borderColor: "var(--wofy-border)" }}
                      />
                    ) : (
                      <input
                        type={typeof editing[f] === "number" ? "number" : "text"}
                        value={editing[f] ?? ""}
                        onChange={(e) => setEditing({ ...editing, [f]: typeof editing[f] === "number" ? Number(e.target.value) : e.target.value })}
                        className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent"
                        style={{ borderColor: "var(--wofy-border)" }}
                      />
                    )}
                  </label>
                ))}
                <div className="flex gap-2">
                  <button onClick={saveEdit} data-testid="admin-sub-save" className="wofy-btn-primary text-sm"><Save className="w-4 h-4" />Save</button>
                  <button onClick={() => setEditing(null)} className="wofy-btn-ghost text-sm"><X className="w-4 h-4" />Cancel</button>
                </div>
              </div>
            )}

            {/* Details preview */}
            {!editing && (
              <div className="mt-6 wofy-card">
                {selected.image_url && <img src={selected.image_url} alt="" className="w-full rounded-lg mb-3" />}
                <p className="text-sm whitespace-pre-line">{selected.body || selected.description || "—"}</p>
              </div>
            )}

            {/* History */}
            <div className="mt-6">
              <div className="text-xs font-mono uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
                <History className="w-3 h-3" /> Audit history
              </div>
              <ol className="mt-3 space-y-3">
                {history.length === 0 && <li className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>No history yet.</li>}
                {history.map((ev) => (
                  <li key={ev.id} className="border-l-2 pl-3 py-1" style={{ borderColor: "var(--wofy-primary)" }}>
                    <div className="text-xs font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>
                      {new Date(ev.created_at).toLocaleString()} · {ev.action}
                    </div>
                    {ev.meta?.patch_keys && <div className="text-xs">Changed: {ev.meta.patch_keys.join(", ")}</div>}
                    {ev.comment && <div className="text-sm mt-1">"{ev.comment}"</div>}
                    <div className="text-[11px] mt-1" style={{ color: "var(--wofy-text-soft)" }}>— {ev.admin_name}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
